/**
 * SendMassage — the app-wide "send message" dialog (email or WhatsApp to one or many customers).
 * Opened from anywhere with the Redux action:
 *   setShowSendMassage(true, "id,id,…"[, "WHATSAPP"])   → open (optionally on the WhatsApp tab)
 *   setShowSendMassage(false)                           → close
 * Rendered once in containers/App.js.
 *
 * Email:
 *   - "תבנית" mode: pick an email template; {{firstName}} {{lastname}} {{fullname}} {{phone}}
 *     {{mail}} {{email}} are replaced per customer, then sent one by one (sendMassageFromServer)
 *   - "טקסט חופשי" mode: subject + rich text (Quill); [name] → customer's first name
 *   - attachments are uploaded first (uploadAttachment) and sent by id
 * WhatsApp:
 *   - plain text, [name] supported; queued on the server (sendWhatsappBatch, 20–60s random gaps)
 * Recipients: customers without a valid email (or Israeli phone for WhatsApp) are skipped
 * automatically; any recipient can be switched off manually ("dontSend").
 *
 * Logic is unchanged from the pre-redesign dialog; only the UI moved to the shared kit (src/ui).
 */
import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { MailOutlined, WhatsAppOutlined } from '@ant-design/icons';

import * as actionsCreators from './../../actions/index.js';
import { Modal, Button, Input, Select, Segmented, Switch, Field, EmptyState } from '../../ui';
import validationEmail from './../../utils/validationEmail.js';
import validationIsraelyPhone from './../../utils/validationIsraelyPhone.js';
import AttachmentPicker from './../../components/AttachmentPicker';
import classes from './sendMassage.css';

const QUILL_MODULES = {
  toolbar: [
    [{ direction: 'rtl' }],
    [{ align: [] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { background: [] }],
    [{ font: [] }, { size: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['direction', 'align', 'bold', 'italic', 'underline', 'color', 'background', 'font', 'size', 'list', 'bullet', 'link'];

const SendMassage = (props) => {
  const [channelMode, setChannelMode] = useState('EMAIL'); // "EMAIL" or "WHATSAPP"
  const [emailMode, setEmailMode] = useState('TEMPLATE'); // "TEMPLATE" or "RICH_TEXT"
  const [subject, setSubject] = useState('');
  const [customersList, setCustomersList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [richTextContent, setRichTextContent] = useState('');
  const [waMessage, setWaMessage] = useState('');
  const [waSending, setWaSending] = useState(false);
  const [emailSending, setEmailSending] = useState(false); // UI only: button spinner while the per-customer loop runs
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Open on the tab the caller asked for (setShowSendMassage(true, ids, "WHATSAPP") → WhatsApp tab)
  useEffect(() => {
    if (props.showSendMassage) setChannelMode(props.sendMassageChannel === 'WHATSAPP' ? 'WHATSAPP' : 'EMAIL');
  }, [props.showSendMassage, props.sendMassageChannel]);

  // Load the recipients whenever the dialog opens / the id list changes
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      if (!props.showSendMassage) return;
      try {
        const cusList = await props.getCustomersListByString(props.sendMassageCustomersList);
        if (isMounted) setCustomersList(cusList);
      } catch (error) {
        if (isMounted) console.error('Error loading customers:', error);
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.sendMassageCustomersList, props.showSendMassage]);

  // Load email templates whenever the dialog opens
  useEffect(() => {
    let isMounted = true;
    const loadTemplates = async () => {
      if (!props.showSendMassage) return;
      try {
        const templatesList = await props.getEmailTemplates();
        if (isMounted) setTemplates(templatesList);
      } catch (error) {
        if (isMounted) console.error('Error loading templates:', error);
      }
    };
    loadTemplates();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showSendMassage]);

  const handleClose = () => {
    props.setShowSendMassage(false, '');
    setChannelMode('EMAIL');
    setEmailMode('TEMPLATE');
    setSubject('');
    setSelectedTemplateId('');
    setRichTextContent('');
    setWaMessage('');
    setCustomersList([]);
    setAttachments([]);
  };

  const handleAddAttachment = async (file) => {
    setUploading(true);
    try {
      const result = await props.uploadAttachment(file);
      if (result.success && result.uploaded && result.uploaded.length > 0) {
        setAttachments((prev) => [...prev, ...result.uploaded]);
      } else if (result.errors && result.errors.length > 0) {
        props.showAlert(result.errors[0].error);
      }
    } catch (error) {
      console.error('Upload error:', error);
      props.showAlert('שגיאה בהעלאת הקובץ');
    }
    setUploading(false);
  };

  const handleRemoveAttachment = async (id) => {
    try {
      await props.deleteAttachment(id);
      setAttachments((prev) => prev.filter((a) => a.id !== id));
    } catch (error) {
      console.error('Delete error:', error);
      props.showAlert('שגיאה במחיקת הקובץ');
    }
  };

  const renderTemplateForCustomer = (template, customer) => {
    // Replace template variables with customer data
    let renderedSubject = template.subject || subject;
    let renderedBody = template.htmlBody || template.bodyHtml || '';

    const replacements = {
      '{{firstName}}': customer.firstname || '',
      '{{lastname}}': customer.lastname || '',
      '{{fullname}}': `${customer.firstname || ''} ${customer.lastname || ''}`.trim(),
      '{{phone}}': customer.phone || '',
      '{{mail}}': customer.mail || '',
      '{{email}}': customer.mail || '',
    };

    Object.keys(replacements).forEach((key) => {
      const regex = new RegExp(key, 'g');
      renderedSubject = renderedSubject.replace(regex, replacements[key]);
      renderedBody = renderedBody.replace(regex, replacements[key]);
    });

    return { subject: renderedSubject, body: renderedBody };
  };

  const onSendWhatsapp = async () => {
    if (!waMessage.trim()) {
      props.showAlert('אנא הכנס הודעה');
      return;
    }
    const cusList = getFilterdList();
    if (cusList.length === 0) {
      props.showAlert('אין לקוחות עם טלפון תקין');
      return;
    }
    setWaSending(true);
    try {
      const customerIds = cusList.map((c) => c.id);
      await props.sendWhatsappBatch(customerIds, waMessage);
      props.showAlert(`${cusList.length} הודעות נוספו לתור WhatsApp`);
      handleClose();
    } catch (e) {
      const msg = (e.response && e.response.data && e.response.data.error) || 'תקלה בשליחת WhatsApp';
      props.showAlert(msg);
    }
    setWaSending(false);
  };

  const onSendMassage = async () => {
    if (channelMode === 'WHATSAPP') {
      return onSendWhatsapp();
    }

    const cusList = getFilterdList();

    if (cusList.length === 0) {
      props.showAlert('אין לקוחות תקינים לשליחה');
      return;
    }

    // Get attachment IDs for sending
    const attachmentIds = attachments.map((a) => a.id);

    setEmailSending(true);
    try {
      if (emailMode === 'TEMPLATE') {
        if (!selectedTemplateId) {
          props.showAlert('אנא בחר תבנית');
          setEmailSending(false);
          return;
        }

        const template = templates.find((t) => t.id === parseInt(selectedTemplateId));
        if (!template) {
          props.showAlert('תבנית לא נמצאה');
          setEmailSending(false);
          return;
        }

        // Send email to each customer with their personalized template
        for (const cus of cusList) {
          const { subject: renderedSubject, body: renderedBody } = renderTemplateForCustomer(template, cus);
          await props.sendMassageFromServer({
            isMail: true,
            body: renderedBody,
            subject: renderedSubject,
            customersList: [cus],
            attachmentIds: attachmentIds,
          });
        }
      } else {
        // RICH_TEXT mode
        if (!subject) {
          props.showAlert('אנא הכנס נושא');
          setEmailSending(false);
          return;
        }
        if (!richTextContent) {
          props.showAlert('אנא הכנס תוכן הודעה');
          setEmailSending(false);
          return;
        }

        // Send rich text email to each customer ([name] → first name). History is added by the backend.
        for (const cus of cusList) {
          const personalizedContent = richTextContent.replace(/\[name\]/g, cus.firstname);
          await props.sendMassageFromServer({
            isMail: true,
            body: personalizedContent,
            subject: subject,
            customersList: [cus],
            attachmentIds: attachmentIds,
          });
        }
      }

      props.showAlert('נשלח בהצלחה');
      handleClose();
    } catch (error) {
      console.error('Error sending messages:', error);
      props.showAlert('תקלה בזמן שליחת ההודעה');
    }
    setEmailSending(false);
  };

  // A recipient can receive on the current channel (valid email / Israeli phone) and wasn't switched off.
  const isReachable = (cus) => (channelMode === 'WHATSAPP' ? validationIsraelyPhone(cus.phone) : validationEmail(cus.mail));

  const getFilterdList = () => customersList.filter((cus) => isReachable(cus) && cus.dontSend !== true);

  const onDontSendSwitch = (index) => {
    const customersList_ = [...customersList];
    customersList_[index].dontSend = customersList_[index].dontSend ? false : true;
    setCustomersList(customersList_);
  };

  const isWhatsapp = channelMode === 'WHATSAPP';
  const sending = isWhatsapp ? waSending : emailSending;
  const selectedTemplate = templates.find((t) => t.id === parseInt(selectedTemplateId));
  const recipientsCount = getFilterdList().length;

  return (
    <Modal
      open={props.showSendMassage}
      onCancel={handleClose}
      maskClosable={false}
      width={760}
      title={isWhatsapp ? 'שליחת WhatsApp' : 'שליחת אימייל'}
      footer={
        <div className={classes.footer}>
          <span className={classes.footerCount}>
            {recipientsCount === 0 ? 'אין נמענים לשליחה' : `ישלח ל-${recipientsCount} ${recipientsCount === 1 ? 'נמען' : 'נמענים'}`}
          </span>
          <Button onClick={handleClose}>ביטול</Button>
          <Button
            type="primary"
            icon={isWhatsapp ? <WhatsAppOutlined /> : <MailOutlined />}
            loading={sending}
            disabled={recipientsCount === 0}
            onClick={sending ? undefined : onSendMassage}
          >
            {isWhatsapp ? (waSending ? 'שולח…' : 'שלח WhatsApp') : 'שלח'}
          </Button>
        </div>
      }
    >
      <div className={classes.body}>
        <Segmented
          block
          value={channelMode}
          onChange={setChannelMode}
          options={[
            { value: 'EMAIL', label: 'אימייל', icon: <MailOutlined /> },
            { value: 'WHATSAPP', label: 'WhatsApp', icon: <WhatsAppOutlined /> },
          ]}
        />

        {isWhatsapp ? (
          <Field label="הודעה" hint="[name] יוחלף בשם הלקוח · השהייה אקראית של 20–60 שניות בין הודעות">
            <Input.TextArea
              autoSize={{ minRows: 5, maxRows: 12 }}
              value={waMessage}
              onChange={(e) => setWaMessage(e.target.value)}
              placeholder="כתוב את ההודעה…"
            />
          </Field>
        ) : (
          <>
            <Segmented
              value={emailMode}
              onChange={setEmailMode}
              options={[
                { value: 'TEMPLATE', label: 'תבנית אימייל' },
                { value: 'RICH_TEXT', label: 'טקסט חופשי' },
              ]}
            />

            {emailMode === 'TEMPLATE' ? (
              <>
                <Field label="תבנית">
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="בחר תבנית…"
                    value={selectedTemplateId || undefined}
                    onChange={(v) => setSelectedTemplateId(String(v))}
                    options={templates.map((t) => ({ value: String(t.id), label: t.name }))}
                  />
                </Field>
                {selectedTemplate ? (
                  <div className={classes.preview}>
                    <div className={classes.previewSubject}>
                      <span className={classes.previewLabel}>נושא</span>
                      {selectedTemplate.subject}
                    </div>
                    <div
                      className={classes.previewBody}
                      dangerouslySetInnerHTML={{ __html: selectedTemplate.htmlBody || selectedTemplate.bodyHtml }}
                    />
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <Field label="נושא">
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="נושא ההודעה" />
                </Field>
                <Field label="תוכן" hint="[name] יוחלף בשם הלקוח">
                  <div className={classes.quill}>
                    <ReactQuill
                      theme="snow"
                      value={richTextContent}
                      onChange={setRichTextContent}
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      placeholder="כתוב את ההודעה כאן…"
                    />
                  </div>
                </Field>
              </>
            )}

            <AttachmentPicker
              attachments={attachments}
              onAdd={handleAddAttachment}
              onRemove={handleRemoveAttachment}
              uploading={uploading}
              disabled={waSending}
            />
          </>
        )}

        {/* Recipients */}
        <div>
          <div className={classes.recipientsHead}>
            <span className={classes.recipientsTitle}>נמענים</span>
            <span className={`${classes.recipientsCount} ui-tabular`}>
              {recipientsCount} מתוך {customersList.length}
            </span>
          </div>
          {customersList.length === 0 ? (
            <EmptyState compact title="אין נמענים" />
          ) : (
            <ul className={classes.recipients}>
              {customersList.map((cus, i) => {
                const reachable = isReachable(cus);
                const on = reachable && !cus.dontSend;
                const contact = isWhatsapp ? cus.phone : cus.mail;
                return (
                  <li key={cus.id || i} className={classes.recipient} data-on={on}>
                    <span className={classes.recipientName}>
                      {[cus.firstname, cus.lastname].filter(Boolean).join(' ') || '—'}
                    </span>
                    <span className={classes.recipientContact} dir="ltr">
                      {contact || '—'}
                    </span>
                    {reachable ? (
                      <Switch size="small" checked={!cus.dontSend} onChange={() => onDontSendSwitch(i)} />
                    ) : (
                      <span className={classes.unreachable}>{isWhatsapp ? 'אין טלפון תקין' : 'אין מייל תקין'}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    showSendMassage: state.ShowModels.showSendMassage,
    sendMassageCustomersList: state.ShowModels.sendMassageCustomersList,
    sendMassageChannel: state.ShowModels.sendMassageChannel,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getCustomersListByString: actionsCreators.getCustomersListByString,
      setShowSendMassage: actionsCreators.setShowSendMassage,
      showAlert: actionsCreators.showAlert,
      sendMassageFromServer: actionsCreators.sendMassageFromServer,
      getEmailTemplates: actionsCreators.getEmailTemplates,
      sendWhatsappBatch: actionsCreators.sendWhatsappBatch,
      uploadAttachment: actionsCreators.uploadAttachment,
      deleteAttachment: actionsCreators.deleteAttachment,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(SendMassage);
